/**
 * Scotland Yard `GameModule` — the platform's entry point for this game.
 *
 * The platform consumes a single `GameModule` per registered game; it knows
 * nothing about Scotland Yard's specifics other than what flows through this
 * file. The module advertises:
 *   - identity (id, displayName, min/max players),
 *   - default settings (currently just the Mr. X selection policy),
 *   - a pure `setupGame` adapter that translates a lobby `Room` into the
 *     engine's `SetupInput`, calls the engine, and returns the engine's
 *     `{ public, mrxStation }` pair packaged as the module's `TState`,
 *   - the host (TV) and player (phone) gameplay components.
 *
 * The module's `TState` is `{ public: GameState; mrxStation: number }` — the
 * SAME shape returned by the engine's `setupGame`. We keep them combined here
 * so the platform doesn't have to know about the public/private split. The
 * platform service layer is responsible for:
 *   - on game start: write `state.public` to `rooms/{code}/state/main` and
 *     `state.mrxStation` to `rooms/{code}/private/mrx`.
 *   - on per-client subscribe: re-merge by reading the `state/main` doc plus
 *     (for Mr. X only) the `private/mrx` doc, and feed the combined object
 *     to the components below.
 *
 * Detectives MUST receive a `mrxStation` value of -1 (or any sentinel) — the
 * Mr. X gameplay components are the only ones that read it, and the
 * `PlayerGameView` short-circuits the read for non-Mr.-X players. The
 * platform layer chooses the sentinel and is responsible for never sending
 * the real value to a detective client.
 */

import type { GameModule } from '../module'
import { setupGame as engineSetup } from '@mdev/common'
import type { SetupPlayer, GameState } from '@mdev/common'
import { HostFinishedView } from './components/HostFinishedView'
import { HostGameView } from './components/HostGameView'
import { HostLobbyExtras } from './components/HostLobbyExtras'
import { PlayerFinishedView } from './components/PlayerFinishedView'
import { PlayerGameView } from './components/PlayerGameView'
import { subscribePlayerState } from './service'

/**
 * Game-specific settings stored on the room document. Currently just lets
 * the host control how Mr. X is chosen at start-of-game time.
 *
 *   - `'random'`: pick uniformly at random from the joined players.
 *   - `{ uid }`: a specific player has been designated Mr. X by the host.
 */
/** Detective starting-ticket variants (difficulty knob). */
export type DetectiveTicketMode = 'standard' | 'double-taxi' | 'double-all' | 'unlimited'

/** Mr. X ticket rule-set. Classic = MB 1985 (5 black / 2 double, fixed);
 * Ravensburger = 2013+ (black = detective count, 3 double). */
export type SyEdition = 'classic' | 'ravensburger'

export interface ScotlandYardSettings {
  mrXSelection: 'random' | { uid: string }
  detectiveTickets: DetectiveTicketMode
  edition: SyEdition
}

/** Sentinel ticket count for "unlimited" — high enough that a full game of
 * decrements (≤24 moves) never brings it near the ∞ display threshold. */
export const UNLIMITED_TICKETS = 999
const UNLIMITED_DISPLAY_THRESHOLD = 900

/** Render a ticket count, showing ∞ for the effectively-unlimited sentinel. */
export function formatTicketCount( count: number ): string {
  return count >= UNLIMITED_DISPLAY_THRESHOLD ? '∞' : String( count )
}

/** Resolve a detective ticket mode to concrete starting counts. */
export function detectiveTicketCounts( mode: DetectiveTicketMode ) {
  switch ( mode ) {
    case 'double-taxi':
      return { taxi: 22, bus: 8, underground: 4 }
    case 'double-all':
      return { taxi: 22, bus: 16, underground: 8 }
    case 'unlimited':
      return { taxi: UNLIMITED_TICKETS, bus: UNLIMITED_TICKETS, underground: UNLIMITED_TICKETS }
    case 'standard':
    default:
      return { taxi: 11, bus: 8, underground: 4 }
  }
}

/** Resolve Mr. X's black/double counts from the edition + detective count. */
export function mrxTicketCounts( edition: SyEdition, detectiveCount: number ) {
  if ( edition === 'ravensburger' ) {
    return { mrxBlackTickets: detectiveCount, mrxDoubleMoves: 3 }
  }
  return { mrxBlackTickets: 5, mrxDoubleMoves: 2 }
}

/** TState shape for SY: combines the public game state with Mr. X's secret. */
export interface ScotlandYardState {
  public: GameState
  mrxStation: number
}

export const scotlandYardModule: GameModule<ScotlandYardState, ScotlandYardSettings> = {
  id: 'scotland-yard',
  displayName: 'Scotland Yard',
  // 1 Mr. X + 2-5 detectives = 3-6 players (rules.md "Players").
  minPlayers: 3,
  maxPlayers: 6,
  defaultSettings: {
    mrXSelection: 'random',
    detectiveTickets: 'standard',
    edition: 'classic',
  },

  setupGame: ( room ) => {
    // Stable seating order: lobby join time. Object.entries is iteration-
    // order-preserving for string keys in modern JS, but we sort defensively
    // by lastSeenMs (lobby join records `lastSeenMs = Date.now()`) so two
    // hosts on different runtimes produce the same order.
    const players = Object.values( room.players )
      // The host laptop is a display, not a player — never deal it a role,
      // even if a legacy room still carries the host in its players map.
      .filter( ( p ) => p.uid !== room.hostUid )
      .slice()
      .sort( ( a, b ) => {
      // Tie-break by uid so equal timestamps are still deterministic.
      if ( a.lastSeenMs !== b.lastSeenMs ) return a.lastSeenMs - b.lastSeenMs
      return a.uid < b.uid ? -1 : 1
    } )

    if ( players.length < 3 || players.length > 6 ) {
      throw new Error(
        `Scotland Yard requires 3-6 players (got ${ players.length }).`,
      )
    }

    // Pick Mr. X per settings.
    const mrxUid = ( () => {
      const setting = room.settings.mrXSelection
      if ( setting !== 'random' ) {
        const found = players.find( ( p ) => p.uid === setting.uid )
        if ( !found ) {
          throw new Error(
            `Mr. X uid ${ setting.uid } not in player list.`,
          )
        }
        return setting.uid
      }
      const idx = Math.floor( Math.random() * players.length )
      return players[ idx ].uid
    } )()

    // Coalesce any missing profile fields to safe defaults — a player with an
    // unset color/avatar/name must never produce an `undefined` in the game
    // state doc (Firestore rejects undefined).
    const mrxLobby = players.find( ( p ) => p.uid === mrxUid )!
    const detectives: SetupPlayer[] = players
      .filter( ( p ) => p.uid !== mrxUid )
      .map( ( p ) => ( {
        uid: p.uid,
        displayName: p.displayName ?? 'Player',
        color: p.color ?? 'red',
        avatar: p.avatar ?? '🕵️',
      } ) )

    const { mrxBlackTickets, mrxDoubleMoves } = mrxTicketCounts(
      room.settings.edition ?? 'classic',
      detectives.length,
    )

    const { state, mrxStation } = engineSetup( {
      players: detectives,
      mrx: {
        uid: mrxLobby.uid,
        displayName: mrxLobby.displayName ?? 'Mr. X',
        avatar: mrxLobby.avatar ?? '🕵️',
      },
      detectiveTickets: detectiveTicketCounts( room.settings.detectiveTickets ?? 'standard' ),
      mrxBlackTickets,
      mrxDoubleMoves,
    } )

    return { public: state, mrxStation }
  },

  /**
   * Tell the platform layer how to split SY's `TState` into public + private
   * Firestore docs at game-start. The platform writes whatever buckets we
   * return; we put `state.public` in `state/main` and Mr. X's secret station
   * in `private/mrx` — the same shape the engine + service layer reads back.
   */
  splitInitialState: ( state ) => ( {
    gameStateDoc: state.public,
    privateDocs: {
      // Seed Mr. X's private doc with both his secret station and the
      // single-element history starting at that station. Subsequent
      // service-layer `applyMove` calls grow this array; on game-end the
      // service denormalizes it into `state.public.replay.mrxStations`.
      mrx: { station: state.mrxStation, stationHistory: [ state.mrxStation ] },
    },
  } ),

  /**
   * Per-player live subscription. Public state for everyone; private/mrx
   * additionally for the Mr. X player (identified by uid match against
   * `public.mrx.uid`). Detective clients always see `mrxStation: -1`.
   */
  subscribePlayerState,

  HostGameView,
  PlayerGameView,
  HostLobbyExtras,
  HostFinishedView,
  PlayerFinishedView,
}

export default scotlandYardModule
